import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutSuccess } from '../store/authSlice';
import { RootState } from '../store';
import api from '../services/api';

interface Project {
  _id: string;
  name: string;
}

const HomeScreen: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);

  const [showProjectModal, setShowProjectModal] = useState(false);
  const [clientProjects, setClientProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [selectedProject, setSelectedProject] = useState<string>('');

  const handleLogout = () => {
    console.log('Logging out, clearing localStorage');
    localStorage.clear();
    console.log('localStorage cleared');
    dispatch(logoutSuccess());
    navigate('/login');
  };

  const handleProjectSelect = (projectId: string) => {
    setSelectedProject(projectId);
  };

  const handleProjectConfirm = () => {
    if (selectedProject) {
      navigate(`/map/${selectedProject}`, { replace: true });
    }
  };

  useEffect(() => {
    if (user?.role === 'client') {
      const projects = user.projects || [];
      if (projects.length > 1) {
        // Multi-project client - show selection modal
        setShowProjectModal(true);
        loadClientProjects(projects);
      } else if (projects.length === 1) {
        // Single project client - go directly to project
        navigate(`/map/${projects[0]}`, { replace: true });
      } else {
        // No projects - redirect to profile (might show message)
        navigate('/profile', { replace: true });
      }
    }
  }, [user, navigate]);

  const loadClientProjects = async (projectIds: string[]) => {
    try {
      setLoadingProjects(true);

      // Load all client projects
      const projectPromises = projectIds.map((projectId: string) =>
        api.get(`/projects/${projectId}`)
      );

      const projectResponses = await Promise.all(projectPromises);
      const projects = projectResponses.map((res: any) => res.data);
      setClientProjects(projects);

      // Pre-select first project
      if (projects.length > 0) {
        setSelectedProject(projects[0]._id);
      }
    } catch (error) {
      console.error('Error loading client projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  return (
    <div className="min-h-screen bg-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome, {user?.name ?? 'User'}!
          </h1>
          <p className="text-gray-600">
            Role: <span className="font-medium capitalize">{user?.role}</span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Content hidden for multi-project clients until they select a project */}
        {!showProjectModal && user?.role === 'client' && (user?.projects?.length || 0) === 1 && (
          <>
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold mb-4">View Course</h2>
              <p className="text-gray-600 mb-4">
                Access your assigned golf course map and pins.
              </p>
              <button
                onClick={() => navigate(`/map/${user.projects?.[0]}`)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                View Course
              </button>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold mb-4">Profile</h2>
              <p className="text-gray-600 mb-4">
                View and update your profile information.
              </p>
              <button
                onClick={() => navigate('/profile')}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                My Profile
              </button>
            </div>
          </>
        )}

        {user?.role !== 'client' && (
          <>
            {user?.role === 'admin' && (
              <>
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-lg font-semibold mb-4">User Management</h2>
                  <p className="text-gray-600 mb-4">
                    Manage users and their roles across the system.
                  </p>
                  <button
                    onClick={() => navigate('/users')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Manage Users
                  </button>
                </div>
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-lg font-semibold mb-4">View Projects</h2>
                  <p className="text-gray-600 mb-4">
                    View and manage all golf course projects.
                  </p>
                  <button
                    onClick={() => navigate('/projects')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    View All Projects
                  </button>
                </div>
              </>
            )}

            {user?.role === 'designer' && (
              <>
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h2 className="text-lg font-semibold mb-4">Project Management</h2>
                  <p className="text-gray-600 mb-4">
                    Create and manage golf course projects.
                  </p>
                  <button
                    onClick={() => navigate('/projects')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Manage Projects
                  </button>
                </div>
              </>
            )}

            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-lg font-semibold mb-4">Profile</h2>
              <p className="text-gray-600 mb-4">
                View and update your profile information.
              </p>
              <button
                onClick={() => navigate('/profile')}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                My Profile
              </button>
            </div>
          </>
        )}
        </div>

      </div>

      {/* Project Selection Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-semibold mb-6 text-center">
              Select Project
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Choose which project you want to work on:
                </label>
                {loadingProjects ? (
                  <div className="text-center py-4">
                    <div className="text-gray-600">Loading projects...</div>
                  </div>
                ) : (
                  <select
                    value={selectedProject}
                    onChange={(e) => handleProjectSelect(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    {clientProjects.map((project) => (
                      <option key={project._id} value={project._id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleLogout}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Log Out
                </button>
                <button
                  onClick={handleProjectConfirm}
                  disabled={!selectedProject || loadingProjects}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeScreen;
